import { UserRole } from '@api'
import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { $account } from '@/models/account.ts'
import { useListService } from '@/modules/tasks/service/task-fetch-context'
import { type List } from '@/modules/tasks/service/task-list-service'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

export const listQueryKey = 'task-lists'

export function useLists(options?: { enabled: boolean }) {
  const { list } = useListService()
  const account = useUnit($account)

  return useQuery(
    listQueryKey,
    async () => {
      const lists = await list()

      if (account?.role !== UserRole.TEAM_MEMBER) {
        return lists
      }

      return lists.map((list) => ({
        ...list,
        tasks: list.tasks.filter((task) =>
          task.assignees?.some((assignee) => assignee.id === account.memberId),
        ),
      }))
    },
    options,
  )
}

export function useCreateList() {
  const client = useQueryClient()
  const { create } = useListService()

  return useMutation(create, {
    onMutate: async (list) => {
      const current = client.getQueryData<List[]>(listQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = [...validEntries, list]

        client.setQueryData(listQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(listQueryKey),
  })
}

export function useUpdateList() {
  const client = useQueryClient()
  const { update } = useListService()

  return useMutation(update, {
    onMutate: async (list) => {
      const current = client.getQueryData<List[]>(listQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.map((entry) =>
          entry.id === list.id ? { ...entry, ...list.form } : entry,
        )

        client.setQueryData(listQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(listQueryKey),
  })
}

export function useDeleteList() {
  const client = useQueryClient()
  const { delete: delete_ } = useListService()

  return useMutation(delete_, {
    onMutate: async ({ id }) => {
      const current = client.getQueryData<List[]>(listQueryKey)

      if (current) {
        const validEntries = current.filter(isTruthyAndNotEmpty)
        const updated = validEntries.filter((entry) => entry.id !== id)

        client.setQueryData(listQueryKey, updated)
      }

      return current
    },
    onSettled: () => client.invalidateQueries(listQueryKey),
  })
}

export function useList(id: number) {
  const { find } = useListService()
  const account = useUnit($account)

  return useQuery(['task-list', id], async () => {
    const list = await find(id)

    if (account?.role !== UserRole.TEAM_MEMBER) {
      return list
    }

    return {
      ...list,
      tasks: list.tasks.filter((task) =>
        task.assignees?.some((assignee) => assignee.id === account.memberId),
      ),
    }
  })
}
